import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AdminBillingService, CustomerUsage } from '../../services/billingService';
import { api } from '../../services/api';
import humps from 'humps';
import { Badge } from '../ui';

interface GeneratedMedia {
    id: string;
    type: 'image' | 'video';
    url: string;
    thumbnailUrl?: string;
    status: string;
    creditsConsumed: number;
    createdAt: string;
    campaignId: number;
    campaignName: string;
    taskId?: number;
    resolution?: string;
    duration?: number;
}

interface Transaction {
    id: string;
    type: string;
    amount: number;
    description: string;
    createdAt: string;
}

interface Filters {
    mediaType: 'all' | 'image' | 'video';
    dateFrom: string;
    dateTo: string;
    campaignId: string;
    status: string;
}

const CustomerDetailPage: React.FC = () => {
    const { userId } = useParams<{ userId: string }>();
    const navigate = useNavigate();
    
    const [customerUsage, setCustomerUsage] = useState<CustomerUsage | null>(null);
    const [media, setMedia] = useState<GeneratedMedia[]>([]);
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'media' | 'transactions' | 'campaigns'>('media');
    
    // Media Filters - default to current month
    const [filters, setFilters] = useState<Filters>({
        mediaType: 'all',
        dateFrom: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
        dateTo: new Date().toISOString().split('T')[0],
        campaignId: '',
        status: '',
    });
    
    // Media stats
    const [mediaStats, setMediaStats] = useState({ total: 0, images: 0, videos: 0 });
    
    // Selected campaign for drill-down
    const [selectedCampaignId, setSelectedCampaignId] = useState<number | null>(null);

    // Transaction Filters
    const [txFilters, setTxFilters] = useState({
        type: '',
        startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0], // First day of current month
        endDate: new Date().toISOString().split('T')[0], // Today
    });

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [txPage, setTxPage] = useState(1);
    const [txTotalPages, setTxTotalPages] = useState(1);
    const perPage = 20;

    // Selected media for preview
    const [selectedMedia, setSelectedMedia] = useState<GeneratedMedia | null>(null);
    
    // Export loading
    const [isExporting, setIsExporting] = useState(false);

    useEffect(() => {
        if (userId) {
            loadCustomerData();
        }
    }, [userId]);

    useEffect(() => {
        if (userId && activeTab === 'media') {
            loadMedia();
        }
    }, [userId, filters, currentPage, activeTab]);

    const loadCustomerData = async () => {
        setIsLoading(true);
        try {
            const usage = await AdminBillingService.getCustomerUsage(Number(userId));
            setCustomerUsage(usage);
        } catch (err) {
            console.error('Error loading customer data:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const loadMedia = async (campaignIdOverride?: number) => {
        try {
            const params: any = {
                page: currentPage,
                per_page: perPage,
                user_id: userId,
            };
            
            if (filters.mediaType !== 'all') params.type = filters.mediaType;
            if (filters.dateFrom) params.date_from = filters.dateFrom;
            if (filters.dateTo) params.date_to = filters.dateTo;
            if (campaignIdOverride) {
                params.campaign_id = campaignIdOverride;
            } else if (filters.campaignId) {
                params.campaign_id = filters.campaignId;
            }
            if (filters.status) params.status = filters.status;

            const response = await api.get('/admin/billing/customers/' + userId + '/media', { params });
            const data = humps.camelizeKeys(response.data) as any;
            const mediaItems = data.data || [];
            setMedia(mediaItems);
            setTotalPages(data.meta?.lastPage || 1);
            
            // Calculate stats
            const images = mediaItems.filter((m: GeneratedMedia) => m.type === 'image').length;
            const videos = mediaItems.filter((m: GeneratedMedia) => m.type === 'video').length;
            setMediaStats({
                total: data.meta?.total || mediaItems.length,
                images,
                videos,
            });
        } catch (err) {
            console.error('Error loading media:', err);
            setMedia([]);
            setMediaStats({ total: 0, images: 0, videos: 0 });
        }
    };

    const loadTransactions = async () => {
        try {
            const params: any = {
                page: txPage,
                perPage,
            };
            if (txFilters.type) params.type = txFilters.type;
            if (txFilters.startDate) params.startDate = txFilters.startDate;
            if (txFilters.endDate) params.endDate = txFilters.endDate;
            
            const response = await AdminBillingService.getCustomerTransactions(Number(userId), params);
            setTransactions(response.data);
            setTxTotalPages(response.meta?.lastPage || 1);
        } catch (err) {
            console.error('Error loading transactions:', err);
        }
    };

    const handleExportTransactions = async () => {
        if (!userId) return;
        setIsExporting(true);
        try {
            const params = new URLSearchParams();
            if (txFilters.startDate) params.append('start_date', txFilters.startDate);
            if (txFilters.endDate) params.append('end_date', txFilters.endDate);
            if (txFilters.type) params.append('type', txFilters.type);
            
            const response = await api.get(`/admin/billing/customers/${userId}/transactions/export?${params.toString()}`, {
                responseType: 'blob',
            });
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `extrato_${userId}_${txFilters.startDate}_${txFilters.endDate}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error exporting transactions:', err);
        } finally {
            setIsExporting(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'transactions' && userId) {
            loadTransactions();
        }
    }, [activeTab, userId, txPage, txFilters]);

    const formatNumber = (num: number) => new Intl.NumberFormat('pt-BR').format(num);
    const formatCredits = (num: number) => new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(num);
    const formatDate = (dateStr: string) => new Date(dateStr).toLocaleDateString('pt-BR');
    const formatDateTime = (dateStr: string) => new Date(dateStr).toLocaleString('pt-BR');

    const getStatusVariant = (status: string): 'success' | 'info' | 'error' | 'warning' => {
        const map: Record<string, 'success' | 'info' | 'error' | 'warning'> = {
            completed: 'success',
            processing: 'info',
            failed: 'error',
            pending: 'warning',
        };
        return map[status] ?? 'info';
    };

    const getTxVariant = (type: string): 'error' | 'success' | 'info' | 'warning' => {
        const map: Record<string, 'error' | 'success' | 'info' | 'warning'> = {
            consumption: 'error',
            purchase: 'success',
            initial_credits: 'info',
            manual_credit: 'info',
            expiration: 'warning',
        };
        return map[type] ?? 'info';
    };

    const getTransactionTypeLabel = (type: string) => {
        const labels: Record<string, string> = {
            consumption: 'Consumo',
            purchase: 'Compra',
            initial_credits: 'Créditos Iniciais',
            manual_credit: 'Ajuste Manual',
            expiration: 'Expiração',
            refund: 'Reembolso',
        };
        return labels[type] || type;
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-snapfy-bg via-[#1a0a3e] to-snapfy-bg p-6 flex items-center justify-center">
                <svg className="animate-spin h-12 w-12 text-snapfy-primary" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
            </div>
        );
    }

    if (!customerUsage) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-snapfy-bg via-[#1a0a3e] to-snapfy-bg p-6">
                <div className="text-center text-snapfy-text-secondary py-12">Cliente não encontrado</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-snapfy-bg via-[#1a0a3e] to-snapfy-bg p-6">
            <div className="max-w-7xl mx-auto space-y-6">
                {/* Header */}
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 text-snapfy-text-secondary hover:text-snapfy-text-primary transition-colors"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-snapfy-text-primary">{customerUsage.user.name}</h1>
                        <p className="text-snapfy-text-secondary">{customerUsage.user.email}</p>
                    </div>
                </div>

                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="bg-snapfy-surface rounded-xl p-4 border border-snapfy-border">
                        <p className="text-xs text-snapfy-text-secondary">Saldo Atual</p>
                        <p className="text-xl font-bold text-green-400">{formatCredits(customerUsage.balance.currentBalance)}</p>
                    </div>
                    <div className="bg-snapfy-surface rounded-xl p-4 border border-snapfy-border">
                        <p className="text-xs text-snapfy-text-secondary">Total Comprado</p>
                        <p className="text-xl font-bold text-blue-400">{formatCredits(customerUsage.totals.totalPurchased)}</p>
                    </div>
                    <div className="bg-snapfy-surface rounded-xl p-4 border border-snapfy-border">
                        <p className="text-xs text-snapfy-text-secondary">Total Consumido</p>
                        <p className="text-xl font-bold text-snapfy-accent-soft">{formatCredits(customerUsage.totals.totalConsumed)}</p>
                    </div>
                    <div className="bg-snapfy-surface rounded-xl p-4 border border-snapfy-border">
                        <p className="text-xs text-snapfy-text-secondary">Imagens</p>
                        <p className="text-xl font-bold text-snapfy-text-primary">{formatNumber(customerUsage.totals.totalImages)}</p>
                    </div>
                    <div className="bg-snapfy-surface rounded-xl p-4 border border-snapfy-border">
                        <p className="text-xs text-snapfy-text-secondary">Vídeos</p>
                        <p className="text-xl font-bold text-snapfy-text-primary">{formatNumber(customerUsage.totals.totalVideos)}</p>
                    </div>
                </div>

                {/* Tabs */}
                <div className="border-b border-snapfy-border">
                    <nav className="flex gap-4">
                        <button
                            onClick={() => { setActiveTab('media'); setCurrentPage(1); }}
                            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                                activeTab === 'media'
                                    ? 'border-snapfy-primary text-snapfy-accent-soft'
                                    : 'border-transparent text-snapfy-text-secondary hover:text-snapfy-text-primary'
                            }`}
                        >
                            Mídia Gerada
                        </button>
                        <button
                            onClick={() => { setActiveTab('transactions'); setCurrentPage(1); }}
                            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                                activeTab === 'transactions'
                                    ? 'border-snapfy-primary text-snapfy-accent-soft'
                                    : 'border-transparent text-snapfy-text-secondary hover:text-snapfy-text-primary'
                            }`}
                        >
                            Transações
                        </button>
                        <button
                            onClick={() => { setActiveTab('campaigns'); setCurrentPage(1); }}
                            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                                activeTab === 'campaigns'
                                    ? 'border-snapfy-primary text-snapfy-accent-soft'
                                    : 'border-transparent text-snapfy-text-secondary hover:text-snapfy-text-primary'
                            }`}
                        >
                            Campanhas
                        </button>
                    </nav>
                </div>

                {/* Media Tab */}
                {activeTab === 'media' && (
                    <div className="space-y-4">
                        {/* Stats Cards */}
                        <div className="grid grid-cols-3 gap-4">
                            <div className="bg-snapfy-surface rounded-xl p-4 border border-snapfy-border">
                                <p className="text-xs text-snapfy-text-secondary">Total no Período</p>
                                <p className="text-2xl font-bold text-snapfy-text-primary">{formatNumber(mediaStats.total)}</p>
                                <p className="text-xs text-snapfy-text-secondary mt-1">
                                    {filters.dateFrom && filters.dateTo 
                                        ? `${formatDate(filters.dateFrom)} - ${formatDate(filters.dateTo)}`
                                        : 'Todos os períodos'}
                                </p>
                            </div>
                            <div className="bg-snapfy-surface rounded-xl p-4 border border-snapfy-border">
                                <p className="text-xs text-snapfy-text-secondary">Imagens</p>
                                <p className="text-2xl font-bold text-snapfy-accent-soft">{formatNumber(mediaStats.images)}</p>
                                <p className="text-xs text-snapfy-text-secondary mt-1">
                                    {filters.mediaType === 'all' ? 'Filtro: Todos' : filters.mediaType === 'image' ? 'Filtro: Imagens' : 'Filtro: Vídeos'}
                                </p>
                            </div>
                            <div className="bg-snapfy-surface rounded-xl p-4 border border-snapfy-border">
                                <p className="text-xs text-snapfy-text-secondary">Vídeos</p>
                                <p className="text-2xl font-bold text-blue-400">{formatNumber(mediaStats.videos)}</p>
                                <p className="text-xs text-snapfy-text-secondary mt-1">
                                    {filters.status ? `Status: ${filters.status}` : 'Status: Todos'}
                                </p>
                            </div>
                        </div>

                        {/* Filters */}
                        <div className="bg-snapfy-surface rounded-xl p-4 border border-snapfy-border">
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                                <select
                                    value={filters.mediaType}
                                    onChange={(e) => setFilters({ ...filters, mediaType: e.target.value as any })}
                                    className="px-3 py-2 bg-snapfy-inner border border-snapfy-border rounded-xl text-snapfy-text-primary text-sm focus:ring-2 focus:ring-snapfy-focus focus:outline-none"
                                >
                                    <option value="all">Todos os tipos</option>
                                    <option value="image">Imagens</option>
                                    <option value="video">Vídeos</option>
                                </select>
                                <input
                                    type="date"
                                    value={filters.dateFrom}
                                    onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                                    className="px-3 py-2 bg-snapfy-inner border border-snapfy-border rounded-xl text-snapfy-text-primary text-sm focus:ring-2 focus:ring-snapfy-focus focus:outline-none"
                                    placeholder="Data início"
                                />
                                <input
                                    type="date"
                                    value={filters.dateTo}
                                    onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                                    className="px-3 py-2 bg-snapfy-inner border border-snapfy-border rounded-xl text-snapfy-text-primary text-sm focus:ring-2 focus:ring-snapfy-focus focus:outline-none"
                                    placeholder="Data fim"
                                />
                                <select
                                    value={filters.status}
                                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                                    className="px-3 py-2 bg-snapfy-inner border border-snapfy-border rounded-xl text-snapfy-text-primary text-sm focus:ring-2 focus:ring-snapfy-focus focus:outline-none"
                                >
                                    <option value="">Todos os status</option>
                                    <option value="completed">Concluído</option>
                                    <option value="processing">Processando</option>
                                    <option value="failed">Falhou</option>
                                </select>
                                <button
                                    onClick={() => setFilters({ 
                                        mediaType: 'all', 
                                        dateFrom: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
                                        dateTo: new Date().toISOString().split('T')[0],
                                        campaignId: '', 
                                        status: '' 
                                    })}
                                    className="px-3 py-2 bg-snapfy-inner text-snapfy-text-secondary rounded-xl hover:bg-snapfy-inner transition-colors text-sm"
                                >
                                    Resetar Filtros
                                </button>
                            </div>
                        </div>

                        {/* Media Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                            {media.length > 0 ? media.map((item) => (
                                <div
                                    key={item.id}
                                    onClick={() => setSelectedMedia(item)}
                                    className="bg-snapfy-surface rounded-xl overflow-hidden border border-snapfy-border cursor-pointer hover:border-snapfy-primary transition-colors group"
                                >
                                    <div className="aspect-square relative bg-snapfy-inner">
                                        {item.type === 'image' ? (
                                            <img
                                                src={item.thumbnailUrl || item.url}
                                                alt=""
                                                className="w-full h-full object-cover"
                                                onError={(e) => {
                                                    (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23374151" width="100" height="100"/><text x="50" y="50" text-anchor="middle" dy=".3em" fill="%239CA3AF" font-size="12">Sem imagem</text></svg>';
                                                }}
                                            />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center">
                                                <svg className="w-12 h-12 text-snapfy-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                </svg>
                                            </div>
                                        )}
                                        {/* Type badge */}
                                        <div className={`absolute top-2 left-2 px-2 py-0.5 rounded text-xs font-medium ${
                                            item.type === 'video' ? 'bg-blue-500' : 'bg-snapfy-primary'
                                        } text-white`}>
                                            {item.type === 'video' ? 'Vídeo' : 'Imagem'}
                                        </div>
                                        {/* Status badge */}
                                        <div className="absolute top-2 right-2">
                                            <Badge variant={getStatusVariant(item.status)}>{item.status}</Badge>
                                        </div>
                                        {/* Hover overlay */}
                                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                            </svg>
                                        </div>
                                    </div>
                                    <div className="p-3">
                                        <p className="text-xs text-snapfy-text-secondary truncate">{item.campaignName}</p>
                                        <div className="flex justify-between items-center mt-1">
                                            <span className="text-xs text-snapfy-accent-soft">{formatCredits(item.creditsConsumed)} cr</span>
                                            <span className="text-xs text-snapfy-text-secondary">{formatDate(item.createdAt)}</span>
                                        </div>
                                    </div>
                                </div>
                            )) : (
                                <div className="col-span-full text-center text-snapfy-text-secondary py-12">
                                    Nenhuma mídia encontrada com os filtros selecionados
                                </div>
                            )}
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="flex justify-center gap-2">
                                <button
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                    className="px-4 py-2 bg-snapfy-inner text-snapfy-text-primary rounded-xl hover:bg-snapfy-inner disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Anterior
                                </button>
                                <span className="px-4 py-2 text-snapfy-text-secondary">
                                    {currentPage} / {totalPages}
                                </span>
                                <button
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                    disabled={currentPage === totalPages}
                                    className="px-4 py-2 bg-snapfy-inner text-snapfy-text-primary rounded-xl hover:bg-snapfy-inner disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Próxima
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Transactions Tab */}
                {activeTab === 'transactions' && (
                    <div className="space-y-4">
                        {/* Filters */}
                        <div className="bg-snapfy-surface rounded-xl p-4 border border-snapfy-border">
                            <div className="flex flex-wrap items-center gap-4">
                                <div className="flex items-center gap-2">
                                    <label className="text-sm text-snapfy-text-secondary">De:</label>
                                    <input
                                        type="date"
                                        value={txFilters.startDate}
                                        onChange={(e) => { setTxFilters({ ...txFilters, startDate: e.target.value }); setTxPage(1); }}
                                        className="px-3 py-2 bg-snapfy-inner border border-snapfy-border rounded-xl text-snapfy-text-primary text-sm focus:ring-2 focus:ring-snapfy-focus focus:outline-none"
                                    />
                                </div>
                                <div className="flex items-center gap-2">
                                    <label className="text-sm text-snapfy-text-secondary">Até:</label>
                                    <input
                                        type="date"
                                        value={txFilters.endDate}
                                        onChange={(e) => { setTxFilters({ ...txFilters, endDate: e.target.value }); setTxPage(1); }}
                                        className="px-3 py-2 bg-snapfy-inner border border-snapfy-border rounded-xl text-snapfy-text-primary text-sm focus:ring-2 focus:ring-snapfy-focus focus:outline-none"
                                    />
                                </div>
                                <select
                                    value={txFilters.type}
                                    onChange={(e) => { setTxFilters({ ...txFilters, type: e.target.value }); setTxPage(1); }}
                                    className="px-3 py-2 bg-snapfy-inner border border-snapfy-border rounded-xl text-snapfy-text-primary text-sm focus:ring-2 focus:ring-snapfy-focus focus:outline-none"
                                >
                                    <option value="">Todos os tipos</option>
                                    <option value="consumption">Consumo</option>
                                    <option value="purchase">Compra</option>
                                    <option value="initial_credits">Créditos Iniciais</option>
                                    <option value="manual_credit">Ajuste Manual</option>
                                    <option value="expiration">Expiração</option>
                                </select>
                                <div className="flex-1"></div>
                                <button
                                    onClick={handleExportTransactions}
                                    disabled={isExporting}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                                >
                                    {isExporting ? (
                                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                    ) : (
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                    )}
                                    Exportar Extrato
                                </button>
                            </div>
                        </div>

                        {/* Table */}
                        <div className="bg-snapfy-surface rounded-xl overflow-hidden border border-snapfy-border">
                            <table className="w-full">
                                <thead className="bg-snapfy-inner">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-snapfy-text-secondary uppercase">Data</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-snapfy-text-secondary uppercase">Tipo</th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-snapfy-text-secondary uppercase">Descrição</th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-snapfy-text-secondary uppercase">Créditos</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-snapfy-border">
                                    {transactions.length > 0 ? transactions.map((tx) => (
                                        <tr key={tx.id} className="hover:bg-snapfy-surface-hover/50">
                                            <td className="px-4 py-3 text-sm text-snapfy-text-secondary">{formatDateTime(tx.createdAt)}</td>
                                            <td className="px-4 py-3">
                                                <Badge variant={getTxVariant(tx.type)}>
                                                    {getTransactionTypeLabel(tx.type)}
                                                </Badge>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-snapfy-text-secondary">{tx.description}</td>
                                            <td className={`px-4 py-3 text-sm text-right font-medium ${
                                                tx.amount > 0 ? 'text-green-400' : 'text-red-400'
                                            }`}>
                                                {tx.amount > 0 ? '+' : ''}{formatCredits(tx.amount)}
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan={4} className="px-4 py-8 text-center text-snapfy-text-secondary">
                                                Nenhuma transação encontrada no período selecionado
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {txTotalPages > 1 && (
                            <div className="flex justify-center gap-2">
                                <button
                                    onClick={() => setTxPage(p => Math.max(1, p - 1))}
                                    disabled={txPage === 1}
                                    className="px-4 py-2 bg-snapfy-inner text-snapfy-text-primary rounded-xl hover:bg-snapfy-inner disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Anterior
                                </button>
                                <span className="px-4 py-2 text-snapfy-text-secondary">
                                    {txPage} / {txTotalPages}
                                </span>
                                <button
                                    onClick={() => setTxPage(p => Math.min(txTotalPages, p + 1))}
                                    disabled={txPage === txTotalPages}
                                    className="px-4 py-2 bg-snapfy-inner text-snapfy-text-primary rounded-xl hover:bg-snapfy-inner disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Próxima
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Campaigns Tab */}
                {activeTab === 'campaigns' && (
                    <div className="space-y-4">
                        {/* Back button when viewing campaign media */}
                        {selectedCampaignId && (
                            <button
                                onClick={() => { setSelectedCampaignId(null); setMedia([]); }}
                                className="flex items-center gap-2 text-snapfy-text-secondary hover:text-snapfy-text-primary transition-colors"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                                Voltar para lista de campanhas
                            </button>
                        )}

                        {/* Campaign Media View */}
                        {selectedCampaignId && (
                            <div className="space-y-4">
                                <div className="bg-snapfy-surface rounded-xl p-4 border border-snapfy-border">
                                    <h3 className="text-lg font-medium text-snapfy-text-primary mb-2">
                                        Mídia da Campanha: {customerUsage.usageByCampaign.find(c => c.id === selectedCampaignId)?.name}
                                    </h3>
                                    <p className="text-sm text-snapfy-text-secondary">
                                        {media.length} itens encontrados
                                    </p>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                                    {media.map((item) => (
                                        <div
                                            key={item.id}
                                            onClick={() => setSelectedMedia(item)}
                                            className="bg-snapfy-surface rounded-xl overflow-hidden border border-snapfy-border cursor-pointer hover:border-snapfy-primary transition-colors group"
                                        >
                                            <div className="aspect-square relative bg-snapfy-inner">
                                                {item.type === 'image' ? (
                                                    <img
                                                        src={item.thumbnailUrl || item.url}
                                                        alt=""
                                                        className="w-full h-full object-cover"
                                                        loading="lazy"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center">
                                                        <svg className="w-12 h-12 text-snapfy-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                        </svg>
                                                    </div>
                                                )}
                                                <div className={`absolute top-2 left-2 px-2 py-0.5 rounded text-xs font-medium ${
                                                    item.type === 'video' ? 'bg-blue-500' : 'bg-snapfy-primary'
                                                } text-white`}>
                                                    {item.type === 'video' ? 'Vídeo' : 'Imagem'}
                                                </div>
                                            </div>
                                            <div className="p-3">
                                                <div className="flex justify-between items-center">
                                                    <span className="text-xs text-snapfy-accent-soft">{formatCredits(item.creditsConsumed)} cr</span>
                                                    <span className="text-xs text-snapfy-text-secondary">{formatDate(item.createdAt)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {media.length === 0 && (
                                    <div className="text-center text-snapfy-text-secondary py-12">
                                        Nenhuma mídia gerada nesta campanha
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Campaign List */}
                        {!selectedCampaignId && (
                            <>
                                {(!customerUsage.usageByCampaign || customerUsage.usageByCampaign.length === 0) ? (
                                    <div className="text-center text-snapfy-text-secondary py-12">
                                        Nenhuma campanha encontrada para este usuário
                                    </div>
                                ) : customerUsage.usageByCampaign.map((campaign) => (
                                    <div 
                                        key={campaign.id} 
                                        className="bg-snapfy-surface rounded-xl p-4 border border-snapfy-border hover:border-snapfy-primary transition-colors cursor-pointer"
                                        onClick={() => {
                                            setSelectedCampaignId(campaign.id);
                                            setCurrentPage(1);
                                            loadMedia(campaign.id);
                                        }}
                                    >
                                        <div className="flex items-center justify-between mb-3">
                                            <div>
                                                <h3 className="text-lg font-medium text-snapfy-text-primary">{campaign.name}</h3>
                                                <p className="text-sm text-snapfy-text-secondary">Criada em {formatDate(campaign.createdAt)}</p>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <Badge variant={getStatusVariant(campaign.status)}>
                                                    {campaign.status}
                                                </Badge>
                                                <svg className="w-5 h-5 text-snapfy-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                </svg>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-4 gap-4">
                                            <div>
                                                <p className="text-xs text-snapfy-text-secondary">Consumido</p>
                                                <p className="text-lg font-semibold text-snapfy-accent-soft">{formatCredits(campaign.creditsConsumed)}</p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-snapfy-text-secondary">Estimado</p>
                                                <p className="text-lg font-semibold text-snapfy-text-secondary">{formatCredits(campaign.creditsEstimated)}</p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-snapfy-text-secondary">Imagens</p>
                                                <p className="text-lg font-semibold text-snapfy-text-primary">{campaign.imagesCount}</p>
                                            </div>
                                            <div>
                                                <p className="text-xs text-snapfy-text-secondary">Tasks</p>
                                                <p className="text-lg font-semibold text-snapfy-text-primary">{campaign.tasksCount}</p>
                                            </div>
                                        </div>
                                        {/* Progress bar */}
                                        <div className="mt-3">
                                            <div className="w-full bg-snapfy-inner rounded-full h-2">
                                                <div
                                                    className="bg-snapfy-primary h-2 rounded-full"
                                                    style={{ width: `${Math.min((campaign.creditsConsumed / (campaign.creditsEstimated || 1)) * 100, 100)}%` }}
                                                ></div>
                                            </div>
                                            <p className="text-xs text-snapfy-text-secondary mt-1">
                                                {Math.round((campaign.creditsConsumed / (campaign.creditsEstimated || 1)) * 100)}% do estimado
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* Media Preview Modal */}
            {selectedMedia && (
                <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setSelectedMedia(null)}>
                    <div className="bg-snapfy-surface rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-between items-center p-4 border-b border-snapfy-border">
                            <div>
                                <h3 className="text-lg font-medium text-snapfy-text-primary">
                                    {selectedMedia.type === 'video' ? 'Vídeo' : 'Imagem'} - {selectedMedia.campaignName}
                                </h3>
                                <p className="text-sm text-snapfy-text-secondary">{formatDateTime(selectedMedia.createdAt)}</p>
                            </div>
                            <button onClick={() => setSelectedMedia(null)} className="text-snapfy-text-secondary hover:text-snapfy-text-primary">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="p-4">
                            <div className="aspect-video bg-snapfy-inner rounded-xl overflow-hidden mb-4">
                                {selectedMedia.type === 'image' ? (
                                    <img src={selectedMedia.url} alt="" className="w-full h-full object-contain" />
                                ) : (
                                    <video src={selectedMedia.url} controls className="w-full h-full" />
                                )}
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                <div>
                                    <p className="text-snapfy-text-secondary">Status</p>
                                    <Badge variant={getStatusVariant(selectedMedia.status)}>
                                        {selectedMedia.status}
                                    </Badge>
                                </div>
                                <div>
                                    <p className="text-snapfy-text-secondary">Créditos</p>
                                    <p className="text-snapfy-accent-soft font-medium">{formatCredits(selectedMedia.creditsConsumed)}</p>
                                </div>
                                {selectedMedia.resolution && (
                                    <div>
                                        <p className="text-snapfy-text-secondary">Resolução</p>
                                        <p className="text-snapfy-text-primary">{selectedMedia.resolution}</p>
                                    </div>
                                )}
                                {selectedMedia.duration && (
                                    <div>
                                        <p className="text-snapfy-text-secondary">Duração</p>
                                        <p className="text-snapfy-text-primary">{selectedMedia.duration}s</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomerDetailPage;
